import { z } from "zod";
import { optionalIco } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.lektori` tRPC router.
 *
 * `userId` je text (Better Auth používá text IDs), ne UUID.
 */

/** Vytvoření profilu lektora. */
export const createLektorSchema = z.object({
  userId: z
    .string()
    .min(1, "ID uživatele je povinné")
    .max(255, "ID uživatele musí být 255 znaků nebo méně"),
  specKategorie: z
    .array(z.string().max(50, "Název kategorie musí být 50 znaků nebo méně"))
    .max(20, "Maximálně 20 kategorií")
    .optional(),
  bio: z.string().max(5000, "Bio musí být 5000 znaků nebo méně").optional(),
  hodinovaSazba: z.coerce
    .number()
    .nonnegative("Sazba nesmí být záporná")
    .max(1_000_000, "Sazba je mimo povolený rozsah")
    .optional(),
  /** Sazba za celý den — u HCA běžnější než hodinová. */
  sazbaDenKc: z.coerce
    .number()
    .nonnegative("Sazba nesmí být záporná")
    .max(1_000_000, "Sazba je mimo povolený rozsah")
    .optional(),
  externistaIco: optionalIco(),
  aktivni: z.coerce.boolean().optional(),
});

/**
 * Form-input schéma pro „Nový lektor" UI formulář.
 *
 * `createLektorSchema` je API kontrakt: `specKategorie` je pole a `externistaIco`
 * přesně 8 číslic. V UI se ale specializace zadávají jako čárkou oddělený text
 * a IČO smí zůstat prázdné. Resolver formuláře proto musí validovat tyto *surové*
 * hodnoty — `onSubmit` je následně mapuje na `CreateLektorInput` a server vstup
 * znovu ověří přes `createLektorSchema`.
 *
 * Bez tohoto schématu resolver tiše zablokoval odeslání: text „komunikace"
 * neprošel `z.array(...)` a prázdné IČO „" neprošlo `.regex(...).optional()`,
 * aniž by se zobrazila jakákoli chybová hláška.
 */
export const createLektorFormSchema = createLektorSchema
  .omit({ specKategorie: true, externistaIco: true })
  .extend({
    specKategorie: z
      .string()
      .max(500, "Seznam specializací musí být 500 znaků nebo méně")
      .optional(),
    externistaIco: optionalIco(),
  });

/** Partial update — userId povinné jako klíč. */
export const updateLektorSchema = createLektorSchema.partial().extend({
  userId: z
    .string()
    .min(1, "ID uživatele je povinné")
    .max(255, "ID uživatele musí být 255 znaků nebo méně"),
});

/** Whitelist řazení lektorů — router mapuje klíče na sloupce. */
export const LEKTORI_SORT_KEYS = ["jmeno", "aktivni", "vytvoreno"] as const;
export type LektoriSortKey = (typeof LEKTORI_SORT_KEYS)[number];

/** List s paginací + filtry. */
export const listLektoriSchema = paginationSchema.merge(sortSchema(LEKTORI_SORT_KEYS)).extend({
  aktivni: z.coerce.boolean().optional(),
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
});

/** ID-only schema. */
export const lektorIdSchema = z.object({
  userId: z
    .string()
    .min(1, "ID uživatele je povinné")
    .max(255, "ID uživatele musí být 255 znaků nebo méně"),
});

export type CreateLektorInput = z.infer<typeof createLektorSchema>;
export type CreateLektorFormInput = z.input<typeof createLektorFormSchema>;
export type UpdateLektorInput = z.infer<typeof updateLektorSchema>;
export type ListLektoriInput = z.infer<typeof listLektoriSchema>;
export type LektorIdInput = z.infer<typeof lektorIdSchema>;
